import React, { useState, useEffect, useRef, useMemo } from 'react';
import { translate } from '../lib/utils/translate';
import type { LabelKey } from '@/lib/data/labels';
import { PROGRAMMES_DATA as PROGRAMS } from "../calculator/data";
import { getUniversityKey } from "../common";
import { calcHighestAttainableScore } from "../calculator/calculator";
import { appGetItem, appSetItem } from "@/lib/auth-storage";
import type { JupasProgram } from '../types';
import type { MERResult } from '../types';

// Chevron icons as inline SVG components
const ChevronUpIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// Visualization method selector
// 1 = Original (arrows with gradient)
// 2 = Layered bars (recommended)
// 3 = Box plot style
// 4 = Separate tracks
const SCORE_DISTRIBUTION_METHOD: number = 2;

// Show/hide the past admission scores table
const SHOW_ADMISSION_HISTORY_TABLE: boolean = false;

const MEAN_PROGRAMME_JUPAD_ID_PREFIXES = [
  'SY',
  'SA',
  'SH',
  'ST',
  'SW',
  'SV',
];


interface AdmissionScore {
  year: string;
  LQ: number | string;
  MED: number | string;
  UQ?: number | string;  // Upper Quartile (most universities)
  HA?: number | string;  // Highest Attainable (HKUST)
}

interface Props {
  result: { jupas_id: string };
  /** Display language. Falls back to the stored language when omitted. */
  language?: 'en' | 'zh';
  admissionHistory: AdmissionScore[];
  userMinScore: number;
  userCurrentScore: number;
  userMaxScore: number;

  highestAttainableScore: number;
  selectedScoreType?: 'min' | 'current' | 'max';
  showPastYears?: number; // 0=hidden, 1=latest year only, 2=last 2 years, 3=last 3 years, etc.
  showHeader?: boolean;
  recommendationRate?: number; // Recommendation rate percentage
  ger?: MERResult;
  per?: MERResult;
  scoreBreakdown?: Array<{
    subject: string;
    grade: string;
    baseScore?: number;
    score: number;
    weighting?: number;
    mandatory?: boolean;
    slotType?: string;
    maxScore?: number;
  }>; // Score breakdown for tooltip
}

export const ScoreDistributionBar: React.FC<Props> = ({
  result,
  language,
  admissionHistory,
  userMinScore,
  userCurrentScore,

  userMaxScore,
  highestAttainableScore: rawHighestAttainableScore,
  selectedScoreType = 'current',
  showPastYears = 3,
  showHeader = true,
  recommendationRate,
  ger,
  per,
  scoreBreakdown
}) => {
  // Localised label lookup: `language` comes from the caller so the bar re-renders
  // when the user switches language, instead of reading stored state on its own.
  const t = (key: LabelKey) => translate(key, language);

  const meetsMerRequirements = (ger?.eligible ?? true) && (per?.eligible ?? true);
  const erNotice = !meetsMerRequirements ? t('erNotPassed') : null;

  // Guard against NaN/undefined highestAttainableScore - compute from programme data if invalid
  const highestAttainableScore = useMemo(() => {
    if (typeof rawHighestAttainableScore === 'number' && !isNaN(rawHighestAttainableScore) && rawHighestAttainableScore > 0) {
      return rawHighestAttainableScore;
    }
    // Fallback: compute from programme data
    const jupasId = result?.jupas_id;
    if (jupasId) {
      const program = PROGRAMS.find((p): p is JupasProgram => p.jupas_id === jupasId);
      if (program) {
        const instKey = getUniversityKey(jupasId);
        return calcHighestAttainableScore(program, instKey);
      }
    }
    return 0;
  }, [rawHighestAttainableScore, result?.jupas_id]);
  // debugger;
  // console.log('[ScoreDistributionBar] admissionHistory:', admissionHistory);
  const [activeScoreType, setActiveScoreType] = useState<'min' | 'current' | 'max'>(selectedScoreType);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300); // default fallback

  // Expand/collapse state in the in-memory app store.
  const [isExpanded, setIsExpanded] = useState(() => {
    if (!showHeader) return true;
    if (typeof window !== 'undefined') {
      const stored = appGetItem('collapsible_scoreDistribution');
      return stored !== null ? stored === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    if (!showHeader) return;
    if (typeof window !== 'undefined') {
      appSetItem('collapsible_scoreDistribution', String(isExpanded));
    }
  }, [isExpanded, showHeader]);

  // Sync activeScoreType with selectedScoreType prop when it changes
  useEffect(() => {
    setActiveScoreType(selectedScoreType);
  }, [selectedScoreType]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Check if there's any real admission data
  const hasAdmissionData = admissionHistory.some(score =>
    (typeof score.LQ === 'number' && score.LQ > 0) ||
    (typeof score.MED === 'number' && score.MED > 0) ||
    (typeof score.UQ === 'number' && score.UQ > 0) ||
    (typeof score.HA === 'number' && score.HA > 0)
  );
  // Cap user scores to highestAttainableScore for display purposes (only if highestAttainableScore is valid)
  const displayMinScore = highestAttainableScore > 0 ? Math.min(userMinScore, highestAttainableScore) : userMinScore;
  const displayCurrentScore = highestAttainableScore > 0 ? Math.min(userCurrentScore, highestAttainableScore) : userCurrentScore;
  const displayMaxScore = highestAttainableScore > 0 ? Math.min(userMaxScore, highestAttainableScore) : userMaxScore;

  // If no admission data, show only user scores in text format
  if (!hasAdmissionData) {
    const content = (
      <div className="p-4">
        {erNotice && (
          <div className="mb-3 inline-flex rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
            {erNotice}
          </div>
        )}
        <div className="text-sm text-gray-500 mb-2">{t('newProgrammeNoPastScores')}</div>
        <div className="flex items-center gap-4 text-sm">
          <span className="font-semibold text-gray-700">{t('yourScores')}:</span>
          <span className="text-red-600">
            <span className="text-gray-500">{t('minScore')}:</span> <span className="font-semibold">{displayMinScore.toFixed(2)}</span>
          </span>
          <span className="text-yellow-600">
            <span className="text-gray-500">{t('currentScore')}:</span> <span className="font-semibold">{displayCurrentScore.toFixed(2)}</span>
          </span>
          <span className="text-green-600">
            <span className="text-gray-500">{t('maxScore')}:</span> <span className="font-semibold">{displayMaxScore.toFixed(2)}</span>
          </span>
          <span className="text-gray-400 text-xs">/ {highestAttainableScore}</span>
        </div>
      </div>
    );

    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        {showHeader && (
          <div
            className="flex items-center justify-between px-4 py-3 bg-purple-100 cursor-pointer select-none"
            onClick={() => setIsExpanded(prev => !prev)}
          >
            <h3 className="text-xs font-bold text-purple-700 uppercase tracking-wide">
              Score Distribution
            </h3>
            <button
              onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
              className="p-1 rounded-md hover:bg-purple-200 transition-colors duration-200 text-purple-700"
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            >
              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          </div>
        )}
        {showHeader ? (
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            {content}
          </div>
        ) : (
          content
        )}
      </div>
    );
  }

  // Get the user score based on selected type
  const getUserScore = () => {
    switch (selectedScoreType) {
      case 'min': return userMinScore;
      case 'max': return userMaxScore;
      default: return userCurrentScore;
    }
  };

  const userScore = getUserScore();
  // console.log('**userScore', userScore)

  // debugger;

  const content = (
    <div className="space-y-1 p-3 sm:p-4">
      {erNotice && (
        <div className="mb-3 inline-flex rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
          {erNotice}
        </div>
      )}

      {/* Distribution bar for latest year only - shown if showPastYears > 0 */}
      {showPastYears > 0 && admissionHistory.filter(s => {
        const hasData = (typeof s.MED === 'number' && s.MED > 0) ||
          (typeof s.LQ === 'number' && s.LQ > 0) ||
          (typeof s.UQ === 'number' && s.UQ > 0) ||
          (typeof s.HA === 'number' && s.HA > 0);
        // console.log('[ScoreDistributionBar] Filter check:', { year: s.year, MED: s.MED, LQ: s.LQ, UQ: s.UQ, HA: s.HA, hasData });
        return hasData;
      }).slice(0, 1).map((score, idx) => {
        // Track if values are real or estimated - stick to factual data only
        const hasRealLQ = typeof score.LQ === 'number' && score.LQ > 0;
        const hasRealUQ = typeof score.UQ === 'number' && score.UQ > 0;
        const hasRealHA = typeof score.HA === 'number' && score.HA > 0;
        const MED = typeof score.MED === 'number' ? score.MED : 0;
        // debugger;
        const sMED = MEAN_PROGRAMME_JUPAD_ID_PREFIXES.some(prefix => result.jupas_id.startsWith(prefix)) ? 'MEAN' : 'MED';
        // Only use real data - if LQ/UQ/HA is N/A, use MED as the boundary
        const LQ = hasRealLQ ? (score.LQ as number) : MED;
        // Use HA if available (HKUST), otherwise use UQ
        const UQ = hasRealHA ? (score.HA as number) : (hasRealUQ ? (score.UQ as number) : MED);
        // console.log('[ScoreDistributionBar] Score values:', { year: score.year, hasRealLQ, hasRealUQ, hasRealHA, LQ, MED, UQ, rawHA: score.HA, rawUQ: score.UQ });
        const maxPossible = highestAttainableScore;

        // Use the larger of highestAttainableScore, UQ, MED, and user scores as the scale range
        const range = Math.max(maxPossible, UQ, MED, displayCurrentScore, displayMaxScore);

        // Calculate positions as percentage of scale (0 to max)
        const lqPos = (LQ / range) * 100;
        const medPos = (MED / range) * 100;
        const uqPos = (UQ / range) * 100;
        // Use range for capping instead of maxPossible (which may be 0)
        const userPos = (Math.min(userScore, range) / range) * 100;
        const userMinPos = (Math.min(displayMinScore, range) / range) * 100;
        const userMaxPos = (Math.min(displayMaxScore, range) / range) * 100;

        // Calculate label positions with collision detection
        // Responsive: smaller width on desktop (>= 640px), larger on mobile
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
        const estimatedLabelWidth = isMobile ? 20 : 12; // Width estimate for "MED 29" labels
        const minGap = isMobile ? 2 : 0.8; // Minimum gap between labels

        // Only include labels for real data (not estimated values)
        // Use HA label for HKUST, UQ for others
        const upperLabel = hasRealHA ? 'HA' : 'UQ';
        const labels = [
          ...(hasRealLQ ? [{ key: 'LQ', pos: lqPos, value: LQ, color: 'text-red-600', adjustedPos: lqPos }] : []),
          ...(MED > 0 ? [{ key: 'MED', pos: medPos, value: MED, color: 'text-yellow-600', adjustedPos: medPos }] : []),
          ...((hasRealUQ || hasRealHA) ? [{ key: upperLabel, pos: uqPos, value: UQ, color: 'text-green-600', adjustedPos: uqPos }] : [])
        ].sort((a, b) => a.pos - b.pos);
        // console.log('[ScoreDistributionBar] Labels:', { upperLabel, labels: labels.map(l => ({ key: l.key, value: l.value })) });

        // Iterative collision detection to ensure no overlaps
        const maxIterations = 20;
        let iteration = 0;
        let hasOverlap = true;

        while (hasOverlap && iteration < maxIterations) {
          hasOverlap = false;

          for (let i = 1; i < labels.length; i++) {
            const prevLabel = labels[i - 1];
            const currentLabel = labels[i];

            const prevLabelRight = prevLabel.adjustedPos + estimatedLabelWidth / 2;
            const currentLabelLeft = currentLabel.adjustedPos - estimatedLabelWidth / 2;

            // Check if labels overlap
            if (currentLabelLeft < prevLabelRight + minGap) {
              hasOverlap = true;
              const overlap = (prevLabelRight + minGap) - currentLabelLeft;
              const markerDistance = currentLabel.pos - prevLabel.pos;

              // Adjust based on marker distance
              if (markerDistance < 10) {
                // Very close markers - push both apart equally with more force
                const halfAdjustment = overlap / 2;
                prevLabel.adjustedPos = Math.max(
                  estimatedLabelWidth / 2,
                  prevLabel.adjustedPos - halfAdjustment * 1.2
                );
                currentLabel.adjustedPos = Math.min(
                  100 - estimatedLabelWidth / 2,
                  currentLabel.adjustedPos + halfAdjustment * 1.2
                );
              } else {
                // Far markers - only push current label right
                currentLabel.adjustedPos = Math.min(
                  100 - estimatedLabelWidth / 2,
                  currentLabel.adjustedPos + overlap
                );
              }
            }
          }

          iteration++;
        }

        // Final bounds enforcement
        const maxPosition = 100 - estimatedLabelWidth / 2;
        const minPosition = estimatedLabelWidth / 2;

        for (let i = 0; i < labels.length; i++) {
          labels[i].adjustedPos = Math.max(minPosition, Math.min(maxPosition, labels[i].adjustedPos));
        }

        // One more pass to ensure no overlaps after bounds check
        for (let i = 1; i < labels.length; i++) {
          const prevRight = labels[i - 1].adjustedPos + estimatedLabelWidth / 2;
          const currentLeft = labels[i].adjustedPos - estimatedLabelWidth / 2;

          if (currentLeft < prevRight + minGap) {
            const requiredPos = prevRight + minGap + estimatedLabelWidth / 2;
            if (requiredPos <= maxPosition) {
              labels[i].adjustedPos = requiredPos;
            }
          }
        }

        const labelPositions = labels;

        return (
          <div key={idx} className="mb-4 overflow-hidden">
            {/* Conditional rendering based on SCORE_DISTRIBUTION_METHOD */}
            {SCORE_DISTRIBUTION_METHOD === 1 && (
              /* METHOD 1: Original - Arrows with gradient */
              <div className="relative h-8 bg-gray-100 rounded-lg mx-0.5 mb-6">
                {/* User Score Summary positioned above the arrow range */}
                <div
                  className="absolute bottom-full mb-1 text-xs flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                  style={{
                    left: `${Math.max(0, Math.min(99, userMinPos))}%`,
                    transform: 'translateX(-50%)',
                    marginLeft: `${(userMaxPos - userMinPos) / 2}%`
                  }}
                >
                  <span className="text-green-600">{t('minScore')}: {displayMinScore.toFixed(2)}</span>
                  <span className="text-blue-600 font-bold">{t('currentScore')}: {displayCurrentScore.toFixed(2)}</span>
                  <span className="text-purple-600">{t('maxScore')}: {displayMaxScore.toFixed(2)}</span>
                </div>

                {/* Distribution gradient */}
                <div
                  className="absolute h-full rounded"
                  style={{
                    left: `${lqPos}%`,
                    width: `${Math.max(0, uqPos - lqPos)}%`,
                    background: `linear-gradient(to right, 
                      rgb(239 68 68) 0%, 
                      rgb(234 179 8) ${((medPos - lqPos) / (uqPos - lqPos)) * 100}%, 
                      rgb(34 197 94) 100%)`
                  }}
                />

                {/* Quartile markers */}
                <div className="absolute top-0 bottom-0 w-0.5 bg-red-500" style={{ left: `${lqPos}%` }} />
                {MED > 0 && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-600" style={{ left: `${medPos}%` }} />
                )}
                <div className="absolute top-0 bottom-0 w-0.5 bg-green-500" style={{ left: `${uqPos}%` }} />
                <div className="absolute top-0 bottom-0 w-0.5 bg-gray-400 right-0" />

                {/* User score range */}
                <div className="absolute h-full bg-blue-200 opacity-50 rounded"
                  style={{ left: `${userMinPos}%`, width: `${Math.max(1, userMaxPos - userMinPos)}%` }} />

                {/* User markers */}
                <svg className="absolute top-0 bottom-0" style={{ left: `${Math.max(0, Math.min(99, userMinPos))}%` }} width="2" height="100%">
                  <line x1="1" y1="0" x2="1" y2="100%" stroke="#16a34a" strokeWidth="2" strokeDasharray="4 2" />
                </svg>
                <svg className="absolute top-0 bottom-0" style={{ left: `${Math.max(0, Math.min(98, userPos))}%` }} width="6" height="100%">
                  <line x1="3" y1="0" x2="3" y2="100%" stroke="#2563eb" strokeWidth="6" strokeDasharray="4 2" />
                </svg>
                <svg className="absolute top-0 bottom-0" style={{ left: `${Math.max(0, Math.min(99, userMaxPos))}%` }} width="2" height="100%">
                  <line x1="1" y1="0" x2="1" y2="100%" stroke="#9333ea" strokeWidth="2" strokeDasharray="4 2" />
                </svg>

                {/* Connecting line and arrows */}
                <svg className="absolute top-1/2 -translate-y-1/2 z-10"
                  style={{ left: `${Math.max(0, Math.min(99, userMinPos))}%`, width: `${Math.abs(userMaxPos - userMinPos)}%` }} height="2">
                  <line x1="0" y1="1" x2="100%" y2="1" stroke="white" strokeWidth="2" strokeDasharray="4 2" />
                </svg>
                <svg className="absolute top-1/2 z-10" style={{ left: `${Math.max(0, Math.min(99, userMinPos))}%`, transform: 'translate(0, -50%)' }} width="16" height="16" viewBox="0 0 16 16">
                  <polygon points="0,8 12,2 12,14" fill="white" />
                </svg>
                <svg className="absolute top-1/2 z-10" style={{ left: `${Math.max(0, Math.min(99, userMaxPos))}%`, transform: 'translate(-100%, -50%)' }} width="16" height="16" viewBox="0 0 16 16">
                  <polygon points="16,8 4,2 4,14" fill="white" />
                </svg>

                {/* Labels - only show for real data */}
                {hasRealLQ && (
                  <div className="absolute top-full mt-1 text-xs text-red-600 whitespace-nowrap -translate-x-1/2"
                    style={{ left: `${labelPositions.find(l => l.key === 'LQ')?.adjustedPos}%` }}>
                    <span className="font-semibold">LQ</span> {LQ.toFixed(2)}
                  </div>
                )}
                {MED > 0 && (
                  <div className="absolute top-full mt-1 text-xs text-yellow-600 whitespace-nowrap -translate-x-1/2"
                    style={{ left: `${labelPositions.find(l => l.key === 'MED')?.adjustedPos}%` }}>
                    <span className="font-semibold">{sMED}</span> {MED.toFixed(2)}
                  </div>
                )}
                {hasRealUQ && (
                  <div className="absolute top-full mt-1 text-xs text-green-600 whitespace-nowrap -translate-x-1/2"
                    style={{ left: `${labelPositions.find(l => l.key === 'UQ')?.adjustedPos}%` }}>
                    <span className="font-semibold">UQ</span> {UQ.toFixed(2)}
                  </div>
                )}
              </div>
            )}

            {SCORE_DISTRIBUTION_METHOD === 2 && (

              /* METHOD 2: Layered bars (recommended modern approach) */
              <div className="space-y-0" ref={containerRef}>

                {/* User Score Summary - show only the active score label to avoid overlap */}
                {(() => {
                  // Check if min/max differ from current and are greater than 0
                  const showMin = displayMinScore !== displayCurrentScore && displayMinScore > 0;
                  const showMax = displayMaxScore !== displayCurrentScore && displayMaxScore > 0;

                  // Use pixel-based label positioning to prevent clipping at container edges
                  // Estimate label widths in pixels for accurate clamping
                  const currentLabelPxWidth = 120; // "Current: XX.XX" ~120px
                  const minLabelPxWidth = 80; // "Min: XX.XX" ~80px
                  const maxLabelPxWidth = 80; // "Max: XX.XX" ~80px

                  // Calculate pixel-based positions clamped to container bounds
                  // Center label on marker position, but clamp so label stays fully inside container
                  const currentLeftPx = Math.max(0, Math.min(containerWidth - currentLabelPxWidth, (userPos / 100) * containerWidth - currentLabelPxWidth / 2));
                  const minLeftPx = Math.max(0, Math.min(containerWidth - minLabelPxWidth, (userMinPos / 100) * containerWidth - minLabelPxWidth / 2));
                  const maxLeftPx = Math.max(0, Math.min(containerWidth - maxLabelPxWidth, (userMaxPos / 100) * containerWidth - maxLabelPxWidth / 2));
                  const activeLabel = activeScoreType === 'min' && showMin
                    ? {
                        leftPx: minLeftPx,
                        text: `${t('minScore')}: ${displayMinScore.toFixed(2)}`,
                      }
                    : activeScoreType === 'max' && showMax
                      ? {
                          leftPx: maxLeftPx,
                          text: `${t('maxScore')}: ${displayMaxScore.toFixed(2)}`,
                        }
                      : {
                          leftPx: currentLeftPx,
                          text: `${t('currentScore')}: ${displayCurrentScore.toFixed(2)}`,
                        };

                  return (
                    <>
                      <div className="relative text-xs h-[38px]">
                        <span
                          className="absolute top-2 text-black font-bold whitespace-nowrap"
                          style={{ left: `${activeLabel.leftPx}px` }}
                        >
                          {activeLabel.text}
                        </span>
                      </div>

                    </>
                  );
                })()}

                <div className="relative flex items-center my-3">
                  <div className="relative h-[18px] bg-white rounded-lg border-2 border-gray-200 overflow-visible flex-1">
                    {/* Admission quartile gradient (base layer) - vibrant */}
                    <div
                      className="absolute h-full rounded"
                      style={{
                        left: `${lqPos}%`,
                        width: `${Math.max(0, uqPos - lqPos)}%`,
                        background: `linear-gradient(to right, 
                          rgba(239, 68, 68, 0.9) 0%, 
                          rgba(234, 179, 8, 0.9) ${((medPos - lqPos) / (uqPos - lqPos)) * 100}%, 
                          rgba(34, 197, 94, 0.9) 100%)`
                      }}
                    />

                    {/* Quartile markers - simple vertical lines inside bar, only show for real data */}
                    {hasRealLQ && <div className="absolute top-0 bottom-0 w-0.5 bg-red-600" style={{ left: `${lqPos}%` }} />}
                    <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-700" style={{ left: `${medPos}%` }} />
                    {(hasRealUQ || hasRealHA) && <div className="absolute top-0 bottom-0 w-0.5 bg-green-600" style={{ left: `${uqPos}%` }} />}

                    {/* Always show all markers for webapp to display the range */}
                    {/* Min score marker - thin vertical gray line (1px) solid */}
                    {userMinPos > 0 && (
                      <div
                        className="absolute -top-2 bottom-0 w-[1px] bg-gray-400 z-10"
                        style={{ left: `${userMinPos}%` }}
                      />
                    )}

                    {/* Current score marker (static gray) - always visible */}
                    {userPos > 0 && (
                      <div
                        className="absolute -top-2 bottom-0 w-[1px] bg-gray-400 z-10"
                        style={{ left: `${userPos}%` }}
                      />
                    )}

                    {/* Active score marker - thin vertical black line (2px) that moves based on selection */}
                    <div
                      className="absolute -top-3 bottom-0 w-[2px] bg-black z-20 transition-all duration-500 ease-in-out"
                      style={{
                        left: `${activeScoreType === 'min' ? userMinPos :
                          activeScoreType === 'max' ? userMaxPos :
                            userPos
                          }%`
                      }}
                    />

                    {/* Max score marker - thin vertical gray line (1px) */}
                    {userMaxPos > 0 && (
                      <div
                        className="absolute -top-2 bottom-0 w-[1px] bg-gray-400 z-10"
                        style={{ left: `${userMaxPos}%` }}
                      />
                    )}
                  </div>

                  {/* Max scale marker - outside the bar on the right */}
                  <div className="ml-2 text-xs font-semibold text-gray-500 whitespace-nowrap">
                    {range.toFixed(2)}
                  </div>
                </div>

                {/* Quartile labels - below bar, centered under gradient */}
                {(() => {
                  const gradientCenterPx = ((lqPos + uqPos) / 2 / 100) * containerWidth;
                  const quartileTextWidth = 180; // estimated width of "LQ XX.X MED XX.X UQ XX.X"
                  const maxLeftPx = containerWidth - quartileTextWidth;
                  const clampedLeftPx = Math.max(40, Math.min(gradientCenterPx - quartileTextWidth / 2, maxLeftPx));

                  return (
                    <div className="relative text-xs h-4">
                      {/* Year at left */}
                      <span className="absolute left-0 font-bold text-gray-700 text-[13px]">{score.year}:</span>

                      {/* LQ/MED/UQ centered under gradient block, pixel-based */}
                      <div
                        className="absolute flex items-center gap-2 whitespace-nowrap"
                        style={{ left: `${clampedLeftPx}px` }}
                      >
                        {hasRealLQ && <span className="font-bold text-red-600 text-[13px]">LQ {LQ.toFixed(2)}</span>}
                        {MED > 0 && <span className="font-bold text-yellow-700 text-[13px]">{sMED} {MED.toFixed(2)}</span>}
                        {hasRealHA && <span className="font-bold text-green-600 text-[13px]">HA {UQ.toFixed(2)}</span>}
                        {hasRealUQ && !hasRealHA && <span className="font-bold text-green-600 text-[13px]">UQ {UQ.toFixed(2)}</span>}
                      </div>

                    </div>
                  );
                })()}


                {/* Previous years' scores - shown in same format below with proportional positioning */}
                {showPastYears > 1 && admissionHistory.slice(1, showPastYears).map((prevYear) => {
                  const prevHasRealLQ = typeof prevYear.LQ === 'number' && prevYear.LQ > 0;
                  const prevHasRealUQ = typeof prevYear.UQ === 'number' && prevYear.UQ > 0;
                  const prevHasRealHA = typeof prevYear.HA === 'number' && prevYear.HA > 0;
                  const prevMED = typeof prevYear.MED === 'number' ? prevYear.MED : 0;
                  const prevLQ = prevHasRealLQ ? (prevYear.LQ as number) : prevMED;
                  const prevUQ = prevHasRealHA ? (prevYear.HA as number) : (prevHasRealUQ ? (prevYear.UQ as number) : prevMED);

                  // Skip if no data
                  if (prevMED === 0 && !prevHasRealLQ) return null;

                  // Calculate positions using same scale as the bar
                  const prevLqPos = (prevLQ / range) * 100;
                  const prevUqPos = (prevUQ / range) * 100;
                  const prevCenterPos = (prevLqPos + prevUqPos) / 2;

                  // Calculate pixel position
                  const prevCenterPx = (prevCenterPos / 100) * containerWidth;
                  const prevTextWidth = 180; // estimated width
                  const prevMaxLeftPx = containerWidth - prevTextWidth;
                  const prevClampedLeftPx = Math.max(40, Math.min(prevCenterPx - prevTextWidth / 2, prevMaxLeftPx));

                  return (
                    <div key={prevYear.year} className="relative text-xs h-4 text-gray-500 mt-1">
                      {/* Year at left */}
                      <span className="absolute left-0 font-medium">{prevYear.year}:</span>

                      {/* LQ/MED/UQ pixel-based positioning */}
                      <div
                        className="absolute flex items-center gap-2 whitespace-nowrap"
                        style={{ left: `${prevClampedLeftPx}px` }}
                      >
                        {prevHasRealLQ && <span className="text-red-400/80">LQ {prevLQ.toFixed(2)}</span>}
                        {prevMED > 0 && <span className="text-yellow-600/80">{sMED} {prevMED.toFixed(2)}</span>}
                        {prevHasRealHA && <span className="text-green-500/80">HA {prevUQ.toFixed(2)}</span>}
                        {prevHasRealUQ && !prevHasRealHA && <span className="text-green-500/80">UQ {prevUQ.toFixed(2)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {SCORE_DISTRIBUTION_METHOD === 3 && (
              /* METHOD 3: Box plot style */
              <div className="space-y-2">
                <div className="relative h-16 mx-0.5 mb-6">
                  {/* Admission box plot (top half) */}
                  <div className="absolute top-0 h-6 w-full">
                    <div className="text-xs text-gray-600 mb-1">Admission Scores:</div>
                    {/* IQR box */}
                    <div
                      className="absolute top-4 h-4 border-2 border-gray-600 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200"
                      style={{
                        left: `${lqPos}%`,
                        width: `${Math.max(1, uqPos - lqPos)}%`
                      }}
                    />
                    {/* Median line */}
                    <div
                      className="absolute top-4 h-4 w-0.5 bg-yellow-700"
                      style={{ left: `${medPos}%` }}
                    />
                    {/* Labels - only show for real data */}
                    {hasRealLQ && (
                      <div className="absolute top-9 text-xs text-red-600 -translate-x-1/2" style={{ left: `${lqPos}%` }}>
                        {LQ.toFixed(2)}
                      </div>
                    )}
                    <div className="absolute top-9 text-xs text-yellow-600 -translate-x-1/2" style={{ left: `${medPos}%` }}>
                      {MED.toFixed(2)}
                    </div>
                    {hasRealUQ && (
                      <div className="absolute top-9 text-xs text-green-600 -translate-x-1/2" style={{ left: `${uqPos}%` }}>
                        {UQ.toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* User box plot (bottom half) */}
                  <div className="absolute bottom-0 h-8 w-full">
                    <div className="text-xs text-blue-600 mb-1">{t('yourScoreRange')}:</div>
                    {/* Range box */}
                    <div
                      className="absolute top-4 h-4 border-2 border-blue-500 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100"
                      style={{
                        left: `${userMinPos}%`,
                        width: `${Math.max(1, userMaxPos - userMinPos)}%`
                      }}
                    />
                    {/* Current score marker */}
                    <div
                      className="absolute top-4 h-4 w-1 bg-blue-600"
                      style={{ left: `${userPos}%` }}
                    />
                    <div
                      className="absolute top-5 w-2 h-2 rounded-full bg-blue-600 border border-white -translate-x-1/2"
                      style={{ left: `${userPos}%` }}
                    />
                    {/* Labels */}
                    <div className="absolute top-9 text-xs text-green-600 -translate-x-1/2" style={{ left: `${userMinPos}%` }}>
                      {displayMinScore.toFixed(0)}
                    </div>
                    <div className="absolute top-9 text-xs text-blue-600 font-bold -translate-x-1/2" style={{ left: `${userPos}%` }}>
                      {displayCurrentScore.toFixed(2)}
                    </div>
                    <div className="absolute top-9 text-xs text-purple-600 -translate-x-1/2" style={{ left: `${userMaxPos}%` }}>
                      {displayMaxScore.toFixed(0)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {SCORE_DISTRIBUTION_METHOD === 4 && (
              /* METHOD 4: Separate tracks */
              <div className="space-y-4">
                {/* Admission track */}
                <div>
                  <div className="text-xs text-gray-600 mb-1 font-semibold">Admission Scores</div>
                  <div className="relative h-6 bg-gray-100 rounded-lg">
                    {/* Gradient background */}
                    <div
                      className="absolute h-full rounded-lg"
                      style={{
                        left: `${lqPos}%`,
                        width: `${Math.max(0, uqPos - lqPos)}%`,
                        background: `linear-gradient(to right, 
                          rgb(239 68 68) 0%, 
                          rgb(234 179 8) ${((medPos - lqPos) / (uqPos - lqPos)) * 100}%, 
                          rgb(34 197 94) 100%)`
                      }}
                    />
                    {/* Markers - only show for real data */}
                    {hasRealLQ && <div className="absolute top-1/2 -translate-y-1/2 w-1 h-full bg-red-600" style={{ left: `${lqPos}%` }} />}
                    <div className="absolute top-1/2 -translate-y-1/2 w-1 h-full bg-yellow-700" style={{ left: `${medPos}%` }} />
                    {hasRealUQ && <div className="absolute top-1/2 -translate-y-1/2 w-1 h-full bg-green-600" style={{ left: `${uqPos}%` }} />}
                    {/* Labels - only show for real data */}
                    {hasRealLQ && (
                      <div className="absolute top-full mt-1 text-xs text-red-600 -translate-x-1/2" style={{ left: `${labelPositions.find(l => l.key === 'LQ')?.adjustedPos}%` }}>
                        LQ {LQ.toFixed(2)}
                      </div>
                    )}
                    <div className="absolute top-full mt-1 text-xs text-yellow-600 -translate-x-1/2" style={{ left: `${labelPositions.find(l => l.key === 'MED')?.adjustedPos}%` }}>
                      {sMED} {MED.toFixed(2)}
                    </div>
                    {hasRealUQ && (
                      <div className="absolute top-full mt-1 text-xs text-green-600 -translate-x-1/2" style={{ left: `${labelPositions.find(l => l.key === 'UQ')?.adjustedPos}%` }}>
                        UQ {UQ.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>

                {/* User score track */}
                <div className="mt-4">
                  <div className="text-xs text-blue-600 mb-1 font-semibold">{t('yourScoreRange')}</div>
                  <div className="relative h-6 bg-gray-100 rounded-lg">
                    {/* Range bar */}
                    <div
                      className="absolute h-full rounded-lg"
                      style={{
                        left: `${userMinPos}%`,
                        width: `${Math.max(1, userMaxPos - userMinPos)}%`,
                        background: 'linear-gradient(to right, rgb(220, 38, 38), rgb(234, 179, 8), rgb(34, 197, 94))'
                      }}
                    />
                    {/* Markers */}
                    <div className="absolute top-1/2 -translate-y-1/2 w-1 h-full bg-red-700" style={{ left: `${userMinPos}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-full bg-yellow-700" style={{ left: `${userPos}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-1 h-full bg-green-700" style={{ left: `${userMaxPos}%` }} />
                    {/* Dot on current */}
                    <div
                      className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-yellow-600 border-2 border-white z-10"
                      style={{ left: `${userPos}%` }}
                    />
                    {/* Labels */}
                    <div className="absolute top-full mt-1 text-xs text-green-600 -translate-x-1/2" style={{ left: `${userMinPos}%` }}>
                      Min {displayMinScore.toFixed(0)}
                    </div>
                    <div className="absolute top-full mt-1 text-xs text-blue-600 font-bold -translate-x-1/2" style={{ left: `${userPos}%` }}>
                      You {displayCurrentScore.toFixed(2)}
                    </div>
                    <div className="absolute top-full mt-1 text-xs text-purple-600 -translate-x-1/2" style={{ left: `${userMaxPos}%` }}>
                      Max {displayMaxScore.toFixed(0)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Admission History Table - controlled by SHOW_ADMISSION_HISTORY_TABLE */}
      {SHOW_ADMISSION_HISTORY_TABLE && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs sm:text-sm border border-green-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-green-100 border-b border-green-200">
                <th className="text-left p-1.5 sm:p-2 font-semibold text-green-800">{t('year')}</th>
                <th className="text-center p-1.5 sm:p-2 font-semibold text-green-800">{t('lowerQuartile')}</th>
                <th className="text-center p-1.5 sm:p-2 font-semibold text-green-800">{t('median')}</th>
                <th className="text-center p-1.5 sm:p-2 font-semibold text-green-800">{t('upperQuartile')}</th>
              </tr>
            </thead>
            <tbody>
              {admissionHistory.map((history, idx) => (
                <tr key={history.year} className={`border-b border-green-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-green-50'} hover:bg-green-100 transition-colors`}>
                  <td className={`p-1.5 sm:p-2 ${idx === 0 ? 'font-bold' : ''}`}>{history.year}</td>
                  <td className={`text-center p-1.5 sm:p-2 ${idx === 0 ? 'font-bold' : ''}`}>{typeof history.LQ === 'number' && history.LQ > 0 ? history.LQ : 'N/A'}</td>
                  <td className={`text-center p-1.5 sm:p-2 ${idx === 0 ? 'font-bold' : ''}`}>{typeof history.MED === 'number' && history.MED > 0 ? history.MED : 'N/A'}</td>
                  <td className={`text-center p-1.5 sm:p-2 ${idx === 0 ? 'font-bold' : ''}`}>{typeof history.UQ === 'number' && history.UQ > 0 ? history.UQ : 'N/A'}</td>
                  <td className={`text-center p-1.5 sm:p-2 ${idx === 0 ? 'font-bold' : ''}`}>{typeof history.HA === 'number' && history.HA > 0 ? history.HA : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-lg overflow-hidden">
      {showHeader && (
        <div
          className="flex items-center justify-between px-3 sm:px-4 py-3 bg-purple-100 cursor-pointer select-none"
          onClick={() => setIsExpanded(prev => !prev)}
        >
          <h3 className="text-xs font-bold text-purple-700 uppercase tracking-wide">
            Score Distribution
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
            className="p-1 rounded-md hover:bg-purple-200 transition-colors duration-200 text-purple-700"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </div>
      )}

      {showHeader ? (
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          {content}
        </div>
      ) : (
        content
      )}
    </div>
  );
};
